/*
 * RGM Reels — Phase 1C (minimal public viewing surface)
 * =====================================================
 *
 * Mobile-first vertical Reels feed. One reel per viewport, scroll-snap
 * so the user can swipe through posted content with their thumb.
 *
 * Scope (locked by CEO):
 *   • GET /api/reels/feed              (public, cursor-paginated)
 *   • GET /api/reels/{reel_id}         (deep-link via ?reel_id=...)
 *   • Vertical scroll-snap viewer
 *   • Floating Create button → /create/reel
 *   • iPhone Safari-safe playback via server-derived
 *     media_playback_url (Cloudinary re-mux transformation)
 *
 * Explicitly OUT of scope here:
 *   • gifting, comments, likes, shares
 *   • advanced ranking (feed is strictly newest-first)
 *   • Home preview rail, Artist Profile Reels tab
 *   • financial changes
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import ReelCommentsSheet from "@/components/ReelCommentsSheet";
import ReelOwnerMenu from "@/components/ReelOwnerMenu";
import GiftPicker from "@/components/GiftPicker";
import { optimizeImage } from "@/lib/mediaUrls";

// Single reel tile — a <video> card that lazily plays only when it
// enters the viewport. iOS Safari-safe: muted autoplay by default,
// tap to toggle sound, native controls hidden to keep the swipe
// gesture clean.
function ReelTile({ reel, isActive, currentUser, onReelChanged, onReelDeleted }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  // Phase 1D — local, optimistic social state. All counters are
  // seeded from the feed row so the rail renders without a round-trip.
  const [liked, setLiked] = useState(!!reel.liked);
  const [likeCount, setLikeCount] = useState(reel.like_count || 0);
  const [commentCount, setCommentCount] = useState(reel.comment_count || 0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  const playbackUrl = reel.media_playback_url || reel.media_url;
  // The reel's attached audio playback URL (artist_provided /
  // non-locked own_track). When present we treat it as the
  // authoritative audio track — the video's native audio is muted so
  // we don't play two sources on top of each other.
  const audioUrl = reel.audio_playback_url || null;
  const hasAttachedAudio = !!audioUrl;

  // When the tile becomes the active one (IntersectionObserver in the
  // parent), start playback muted so iOS Safari permits autoplay.
  useEffect(() => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v) return;
    if (isActive) {
      // Reset to the top of the clip on re-entry.
      try { v.currentTime = 0; } catch (_) { /* noop */ }
      try { if (a) a.currentTime = 0; } catch (_) { /* noop */ }
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.then(() => setPlaying(true)).catch(() => {
          // iOS Safari sometimes refuses autoplay — keep showing the
          // poster + a tap-to-play affordance.
          setPlaying(false);
        });
      }
      if (a) { try { a.play().catch(() => {}); } catch (_) {/*noop*/} }
    } else {
      try { v.pause(); } catch (_) { /* noop */ }
      try { if (a) a.pause(); } catch (_) { /* noop */ }
      setPlaying(false);
    }
  }, [isActive]);

  const togglePlay = () => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      if (a) { try { a.play().catch(() => {}); } catch (_) {/*noop*/} }
    } else {
      v.pause();
      if (a) { try { a.pause(); } catch (_) {/*noop*/} }
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    const a = audioRef.current;
    const next = !muted;
    setMuted(next);
    if (v) v.muted = next;
    if (a) a.muted = next;
  };

  const isVideo = (reel.media_kind || "video") === "video";
  const posterUrl = reel.poster_url || null;
  const creatorName = reel.creator?.name || "RGM Artist";
  const creatorId = reel.creator?.user_id || reel.creator_id;
  const isOwner = !!currentUser && creatorId === currentUser.user_id;

  // Phase 1D — refresh authoritative liked/like_count once per
  // activation so a signed-in user sees their own prior like state.
  // Anonymous viewers still get the public count without a round-trip
  // (the feed row already carried it).
  useEffect(() => {
    if (!isActive || !currentUser) return;
    let cancelled = false;
    api.get(`/reels/${reel.reel_id}/like-status`).then((r) => {
      if (cancelled) return;
      if (typeof r.data?.liked === "boolean") setLiked(r.data.liked);
      if (typeof r.data?.like_count === "number") setLikeCount(r.data.like_count);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isActive, currentUser, reel.reel_id]);

  const handleToggleLike = async (e) => {
    e?.stopPropagation?.();
    if (!currentUser) { toast.error("Sign in to like Reels"); return; }
    // optimistic flip; rollback on failure
    const nextLiked = !liked;
    setLiked(nextLiked);
    setLikeCount((c) => c + (nextLiked ? 1 : -1));
    try {
      const r = await api.post(`/reels/${reel.reel_id}/like`);
      if (typeof r.data?.liked === "boolean") setLiked(r.data.liked);
      if (typeof r.data?.like_count === "number") setLikeCount(r.data.like_count);
    } catch (err) {
      setLiked(!nextLiked);
      setLikeCount((c) => c + (nextLiked ? -1 : 1));
      toast.error(err?.response?.data?.detail || "Couldn't update like");
    }
  };

  const handleShare = async (e) => {
    e?.stopPropagation?.();
    // Share the server-side OG URL so external crawlers (Twitter /
    // FB / iMessage / Discord) can render the Reel preview card.
    // The HTML served there has a 0s meta-refresh to the canonical
    // /reels?reel_id= URL so humans never notice the hop.
    const canonical = `${window.location.origin}/reels?reel_id=${encodeURIComponent(reel.reel_id)}`;
    const shareUrl = `${window.location.origin}/api/share/reel/${encodeURIComponent(reel.reel_id)}`;
    const title = reel.caption?.slice(0, 60) || `${creatorName}'s Reel`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url: shareUrl });
        return;  // opening the sheet is not treated as a completed share
      }
    } catch (_) { /* user cancelled or share unavailable */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Reel link copied");
    } catch (_) {
      toast.message("Copy this link:", { description: canonical });
    }
  };

  const handleGiftSent = () => {
    // Reel attribution is server-side only. Nothing to update on the
    // client beyond closing the picker — gift coin accounting is
    // owned by the gift pipeline, NOT by Reels.
    setGiftOpen(false);
  };

  return (
    <section
      className="relative snap-start snap-always w-full h-[100dvh] bg-black flex items-center justify-center overflow-hidden"
      data-testid={`reel-tile-${reel.reel_id}`}
    >
      {isVideo ? (
        <video
          ref={videoRef}
          src={playbackUrl}
          poster={posterUrl || undefined}
          playsInline
          loop
          // When the reel has attached audio, the video must play
          // silent so the <audio> track below is the only audio
          // source. Otherwise follow the user's mute toggle.
          muted={hasAttachedAudio ? true : muted}
          preload="metadata"
          onClick={togglePlay}
          onError={() => setFailed(true)}
          className="w-full h-full object-contain bg-black"
          data-testid={`reel-video-${reel.reel_id}`}
        />
      ) : (
        <img
          src={reel.media_url}
          alt={reel.caption || ""}
          className="w-full h-full object-contain bg-black"
          data-testid={`reel-image-${reel.reel_id}`}
        />
      )}
      {/* Attached audio for image_with_audio or when the reel declares
          a non-video audio playback URL. We keep <audio> hidden; it
          follows the mute toggle + play/pause gesture on the tile. */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          loop
          muted={muted}
          preload="metadata"
          className="hidden"
          data-testid={`reel-audio-${reel.reel_id}`}
        />
      )}

      {/* Playback-failed fallback — rare; keep the user oriented. */}
      {failed && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-6 bg-black/80"
          data-testid={`reel-failed-${reel.reel_id}`}
        >
          <div className="text-4xl mb-2">⚠️</div>
          <div className="text-sm">This Reel couldn't play on your device.</div>
          <a
            href={playbackUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 text-purple-300 text-xs underline"
          >Open directly</a>
        </div>
      )}

      {/* Tap-to-play affordance (iOS Safari autoplay rejection) */}
      {!playing && !failed && isVideo && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/10"
          aria-label="Play"
          data-testid={`reel-tap-play-${reel.reel_id}`}
        >
          <span className="w-16 h-16 rounded-full bg-black/50 flex items-center justify-center text-white text-3xl">▶</span>
        </button>
      )}

      {/* Mute / unmute pill (top-right). Owner menu (⋯) sits next to
          it so it never covers the action rail or the caption. */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        {(isVideo || audioUrl) && (
          <button
            type="button"
            onClick={toggleMute}
            className="text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center text-sm font-black"
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid={`reel-mute-${reel.reel_id}`}
          >{muted ? "🔇" : "🔊"}</button>
        )}
        {isOwner && (
          <ReelOwnerMenu
            reel={reel}
            onUpdated={(fresh) => { if (typeof onReelChanged === "function") onReelChanged(fresh); }}
            onDeleted={(rid) => { if (typeof onReelDeleted === "function") onReelDeleted(rid); }}
          />
        )}
      </div>

      {/* Vertical action rail — Like / Comment / Gift / Share.
          Placed on the right edge so it never covers the caption on
          the left. Each pill is big enough for a thumb tap and keeps
          its own count label underneath. */}
      <div
        className="absolute right-3 bottom-28 sm:bottom-32 z-10 flex flex-col items-center gap-4 text-white"
        data-testid={`reel-action-rail-${reel.reel_id}`}
      >
        <button
          type="button"
          onClick={handleToggleLike}
          className="flex flex-col items-center"
          aria-label={liked ? "Unlike" : "Like"}
          data-testid={`reel-like-${reel.reel_id}`}
        >
          <span className={`w-11 h-11 rounded-full flex items-center justify-center text-xl ${liked ? "bg-pink-600 text-white" : "bg-black/55 text-white"}`}>❤</span>
          <span className="text-xs font-bold mt-0.5" data-testid={`reel-like-count-${reel.reel_id}`}>{likeCount}</span>
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setCommentsOpen(true); }}
          className="flex flex-col items-center"
          aria-label="Comments"
          data-testid={`reel-comment-btn-${reel.reel_id}`}
        >
          <span className="w-11 h-11 rounded-full bg-black/55 flex items-center justify-center text-xl">💬</span>
          <span className="text-xs font-bold mt-0.5" data-testid={`reel-comment-count-${reel.reel_id}`}>{commentCount}</span>
        </button>
        {!isOwner && (
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setGiftOpen(true); }}
              className="w-11 h-11 rounded-full bg-black/55 flex items-center justify-center text-xl"
              aria-label="Send a gift"
              data-testid={`reel-gift-${reel.reel_id}`}
            >🎁</button>
            <span className="text-xs font-bold mt-0.5">Gift</span>
          </div>
        )}
        <button
          type="button"
          onClick={handleShare}
          className="flex flex-col items-center"
          aria-label="Share"
          data-testid={`reel-share-${reel.reel_id}`}
        >
          <span className="w-11 h-11 rounded-full bg-black/55 flex items-center justify-center text-xl">↗</span>
          <span className="text-xs font-bold mt-0.5">Share</span>
        </button>
      </div>

      {/* Hidden mount for the shared GiftPicker — we drive its drawer
          open via its own trigger button, so we render it in a wrapper
          that we open programmatically. The picker reuses the single
          canonical /api/gifts/send endpoint — Reels adds target_type
          'reel' and the backend resolves the creator server-side. */}
      {giftOpen && !isOwner && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center sm:justify-center"
          data-testid={`reel-gift-sheet-${reel.reel_id}`}
          onClick={() => setGiftOpen(false)}
        >
          <div className="absolute inset-0 bg-black/70" />
          <div
            className="relative w-full sm:w-[480px] bg-[#0f0c18] border-t sm:border border-purple-500/30 sm:rounded-xl p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-purple-300">Send a gift</div>
              <button onClick={() => setGiftOpen(false)} className="text-gray-400 text-xl leading-none">×</button>
            </div>
            <GiftPicker
              targetType="reel"
              targetId={reel.reel_id}
              recipientId={creatorId}
              currentUserId={currentUser?.user_id}
              currentUserRole={currentUser?.role}
              onSent={handleGiftSent}
            />
          </div>
        </div>
      )}

      {/* Caption + creator overlay (bottom) */}
      <div
        className="absolute bottom-0 left-0 right-0 p-4 pb-8 bg-gradient-to-t from-black/80 via-black/50 to-transparent text-white"
        data-testid={`reel-meta-${reel.reel_id}`}
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-gray-800 overflow-hidden">
            {reel.creator?.avatar
              ? <img src={optimizeImage(reel.creator.avatar, 'avatar')} alt="" className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-xs">👤</div>}
          </div>
          {creatorId ? (
            <Link
              to={`/artist/${creatorId}`}
              className="text-sm font-black hover:underline"
              onClick={(e) => e.stopPropagation()}
              data-testid={`reel-creator-${reel.reel_id}`}
            >@{creatorName}</Link>
          ) : (
            <span className="text-sm font-black">@{creatorName}</span>
          )}
        </div>
        {reel.caption && (
          <p className="text-sm text-white/90 line-clamp-3" data-testid={`reel-caption-${reel.reel_id}`}>
            {reel.caption}
          </p>
        )}
        {reel.audio_track_meta?.track_title && (
          <div
            className="mt-2 flex items-center gap-2 text-xs text-white/80"
            data-testid={`reel-track-${reel.reel_id}`}
          >
            <span>🎵</span>
            <span className="truncate">
              {reel.audio_track_meta.track_title}
              {reel.audio_track_meta.artist_name
                ? ` · ${reel.audio_track_meta.artist_name}`
                : ""}
            </span>
          </div>
        )}
      </div>
      {/* Phase 1D — Comments bottom sheet. Mounted inside the tile so
          its lifecycle follows the reel in view. */}
      <ReelCommentsSheet
        open={commentsOpen}
        onClose={() => setCommentsOpen(false)}
        reel={reel}
        currentUser={currentUser}
        onCountChange={(n) => setCommentCount(n)}
      />
    </section>
  );
}

export default function ReelsFeedPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const deepLinkId = params.get("reel_id") || null;
  const containerRef = useRef(null);

  const [reels, setReels] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  // Phase 1D — fetch the current user once so the action rail can
  // decide whether to show owner controls, prompt sign-in on gated
  // actions (like / comment / gift), and preselect avatars.
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me").then((r) => {
      if (cancelled) return;
      setCurrentUser(r.data?.user || r.data || null);
    }).catch(() => { if (!cancelled) setCurrentUser(null); });
    return () => { cancelled = true; };
  }, []);

  // Initial load — if a ?reel_id= deep link is present, prepend that
  // specific reel so the user lands on it even if it's older than the
  // first feed page.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [feedRes, deepRes] = await Promise.all([
          api.get("/reels/feed", { params: { limit: 10 } }),
          deepLinkId
            ? api.get(`/reels/${deepLinkId}`).catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const feed = feedRes.data?.reels || [];
        const seen = new Set(feed.map((r) => r.reel_id));
        let combined = feed;
        if (deepRes?.data?.reel && !seen.has(deepRes.data.reel.reel_id)) {
          combined = [deepRes.data.reel, ...feed];
        } else if (deepRes?.data?.reel) {
          // Deep link exists in feed — move it to position 0.
          const idx = combined.findIndex((r) => r.reel_id === deepRes.data.reel.reel_id);
          if (idx > 0) {
            const picked = combined[idx];
            combined = [picked, ...combined.slice(0, idx), ...combined.slice(idx + 1)];
          }
        }
        setReels(combined);
        setCursor(feedRes.data?.next_cursor || null);
        setReachedEnd(!feedRes.data?.next_cursor);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Couldn't load Reels");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deepLinkId]);

  const loadMore = useCallback(async () => {
    if (loadingMore || reachedEnd || !cursor) return;
    setLoadingMore(true);
    try {
      const r = await api.get("/reels/feed", { params: { limit: 10, cursor } });
      const more = r.data?.reels || [];
      setReels((prev) => {
        const have = new Set(prev.map((x) => x.reel_id));
        return [...prev, ...more.filter((x) => !have.has(x.reel_id))];
      });
      setCursor(r.data?.next_cursor || null);
      if (!r.data?.next_cursor) setReachedEnd(true);
    } catch (_) {
      // soft fail — the user can scroll back or retry
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, reachedEnd]);

  // Track which tile is in the viewport so only that reel plays.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || reels.length === 0) return;
    const tiles = root.querySelectorAll("section[data-testid^='reel-tile-']");
    if (!tiles.length) return;
    const io = new IntersectionObserver((entries) => {
      // Pick the entry with the highest intersectionRatio.
      let best = null;
      entries.forEach((e) => {
        if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
      });
      if (best && best.isIntersecting) {
        const idx = Array.from(tiles).indexOf(best.target);
        if (idx >= 0) {
          setActiveIndex(idx);
          // Prefetch next page when the user is within 2 tiles of
          // the current tail.
          if (idx >= reels.length - 2) loadMore();
        }
      }
    }, { root, threshold: [0.5, 0.8] });
    tiles.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, [reels, loadMore]);

  // After deep-link load, scroll the container to the top so the
  // deep-linked reel (placed at index 0) is visible immediately.
  useEffect(() => {
    if (!loading && deepLinkId && containerRef.current) {
      try { containerRef.current.scrollTo({ top: 0 }); } catch (_) {/*noop*/}
    }
  }, [loading, deepLinkId]);

  return (
    <>
      <Helmet>
        <title>Reels · RGM</title>
      </Helmet>
      <div
        className="relative w-full h-[100dvh] bg-black overflow-hidden"
        data-testid="reels-feed-page"
      >
        <div
          ref={containerRef}
          className="w-full h-full overflow-y-auto snap-y snap-mandatory"
          data-testid="reels-feed-scroller"
        >
          {loading && (
            <div
              className="w-full h-[100dvh] flex items-center justify-center text-white/70 text-sm"
              data-testid="reels-feed-loading"
            >Loading Reels…</div>
          )}
          {!loading && error && (
            <div
              className="w-full h-[100dvh] flex flex-col items-center justify-center text-white/80 text-sm p-6 text-center"
              data-testid="reels-feed-error"
            >
              <div className="text-3xl mb-2">⚠️</div>
              <div>{typeof error === "string" ? error : "Couldn't load Reels"}</div>
            </div>
          )}
          {!loading && !error && reels.length === 0 && (
            <div
              className="w-full h-[100dvh] flex flex-col items-center justify-center text-center text-white/80 p-6"
              data-testid="reels-feed-empty"
            >
              <div className="text-5xl mb-3">🎬</div>
              <h2 className="text-xl font-black mb-1">No Reels yet</h2>
              <p className="text-sm text-white/60 max-w-xs">
                Be first to share a 60-second Reel. Record a freestyle or upload a clip.
              </p>
              <Button
                onClick={() => navigate("/create/reel")}
                className="mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black"
                data-testid="reels-feed-empty-create"
              >Create a Reel</Button>
            </div>
          )}
          {reels.map((r, idx) => (
            <ReelTile
              key={r.reel_id}
              reel={r}
              isActive={idx === activeIndex}
              currentUser={currentUser}
              onReelChanged={(fresh) => {
                setReels((prev) => prev.map((x) => x.reel_id === fresh.reel_id ? { ...x, ...fresh } : x));
              }}
              onReelDeleted={(rid) => {
                setReels((prev) => prev.filter((x) => x.reel_id !== rid));
              }}
            />
          ))}
          {loadingMore && (
            <div className="w-full py-4 text-center text-white/60 text-xs" data-testid="reels-feed-loading-more">
              Loading more…
            </div>
          )}
        </div>

        {/* Floating Create button (visible on every state once feed loaded) */}
        {!loading && (
          <button
            type="button"
            onClick={() => navigate("/create/reel")}
            className="absolute bottom-6 right-4 z-20 w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white text-2xl font-black shadow-xl shadow-purple-500/40 flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Create a Reel"
            data-testid="reels-feed-create-fab"
          >+</button>
        )}

        {/* Back pill (top-left) so swiping-in from Discovery feels reversible */}
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/home");
          }}
          className="absolute top-4 left-4 z-20 text-white bg-black/50 rounded-full px-3 py-1.5 text-xs font-bold"
          data-testid="reels-feed-back"
        >‹ Back</button>
      </div>
    </>
  );
}
